import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Spinner,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  TableContainer,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useData } from '../../../data';
import { mutateFunction, useMutation } from '../../../libs/apis';
import { CustomToast } from '../../../components/toast/CustomToast';
import Header from '../../../components/header/Header';
import PermissionBlinder from '../../../components/PermissionBlinder';
import PaginationControls from '../../../components/PaginationControls';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { adminUrl } from '../../../url/urls';
import moment from 'moment';
import FunderForm from './FunderForm';
import BrandButton from '../../../components/brandButton/BrandButton';
import IsDesktop from '../../../utils/IsDesktop';

const initialFormValues = {
  name: '',
  type: '',
  hq_country: '',
  geography_focus: [],
  stage_focus: [],
  sector_focus: [],
  website_url: '',
  application_url: '',
  contact_name: '',
  contact_email: '',
  cheque_min: '',
  cheque_max: '',
  description: '',
  portfolio_examples: [],
  notes: '',
  status: 'active',
};

const createSearchKey = (page, pageSize, searchTerm) => {
  const query = new URLSearchParams({
    page_no: page.toString(),
    page_size: pageSize.toString(),
  });

  if (searchTerm) {
    query.append('search', searchTerm);
  }

  return `admin/funders?${query.toString()}`;
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const isValidUrl = (url) => {
  if (!url) return true;
  try {
    new URL(url);
    return true;
  } catch (err) {
    return false;
  }
};

const Funders = () => {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [selectedFunder, setSelectedFunder] = useState(null);
  const [formValues, setFormValues] = useState(initialFormValues);
  const [formErrors, setFormErrors] = useState({});
  const [formWarnings, setFormWarnings] = useState({});
  const [funders, setFunders] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const accountType = useSelector(
    (state) => state?.user?.userInfo?.userTypeTitle,
  );
  const permissions = useSelector(
    (state) => state?.user?.userInfo?.permissions,
  );
  const navigate = useNavigate();
  const { addToast } = CustomToast();
  const { mutate, isLoading: isMutating } = useMutation(mutateFunction);

  const queryKey = useMemo(
    () => createSearchKey(page, pageSize, searchQuery),
    [page, pageSize, searchQuery],
  );

  const { data: surveydata, isLoading: surveyDataIsLoading } =
    useData('survey');

  const { data, isLoading, refetch } = useData(queryKey, 0, {}, 1, true);

  useEffect(() => {
    if (data) {
      const fetchedFunders = data?.data?.funders || data?.funders || [];
      const pageSizeValue = data?.data?.pageSize || data?.pageSize || pageSize;
      const totalPageValue = data?.data?.totalPages || data?.totalPages || 1;
      const totalItemsValue =
        data?.data?.totalItems || data?.totalItems || fetchedFunders.length;

      setFunders(fetchedFunders);
      setTotalPages(totalPageValue);
      setTotalItems(totalItemsValue);
    }
  }, [data, pageSize]);

  const resetForm = () => {
    setFormValues(initialFormValues);
    setFormErrors({});
    setFormWarnings({});
    setSelectedFunder(null);
  };

  const openCreateForm = () => {
    resetForm();
    setFormMode('create');
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const openEditForm = (funder) => {
    setSelectedFunder(funder);
    setFormMode('edit');
    setFormValues({
      name: funder.name || '',
      type: funder.type || '',
      hq_country: funder.hq_country || '',
      geography_focus: funder.geography_focus || [],
      stage_focus: funder.stage_focus || [],
      sector_focus: funder.sector_focus || [],
      website_url: funder.website_url || '',
      application_url: funder.application_url || '',
      contact_name: funder.contact_name || '',
      contact_email: funder.contact_email || '',
      cheque_min: funder.cheque_min || '',
      cheque_max: funder.cheque_max || '',
      description: funder.description || '',
      portfolio_examples: funder.portfolio_examples || [],
      notes: funder.notes || '',
      status: funder.status || 'inactive',
    });
    setFormErrors({});
    setFormWarnings({});
    setShowForm(true);
  };

  const openViewForm = (funder) => {
    navigate(`/${adminUrl.funderDetail(funder.id)}`);
  };

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleMultiChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const errors = {};
    const warnings = {};

    if (!formValues.name.trim()) {
      errors.name = 'Funder name is required.';
    }

    if (!formValues.type) {
      errors.type = 'Type is required.';
    }

    if (!formValues.hq_country) {
      errors.hq_country = 'HQ country is required.';
    }

    if (!isValidUrl(formValues.website_url)) {
      errors.website_url = 'Website URL must be valid.';
    }

    if (!isValidUrl(formValues.application_url)) {
      errors.application_url = 'Application URL must be valid.';
    }

    if (formValues.contact_email && !isValidEmail(formValues.contact_email)) {
      errors.contact_email = 'Contact email must be valid.';
    }

    const chequeMinValue =
      formValues.cheque_min === '' ? '' : Number(formValues.cheque_min);
    const chequeMaxValue =
      formValues.cheque_max === '' ? '' : Number(formValues.cheque_max);
    if (formValues.cheque_min !== '' && Number.isNaN(chequeMinValue)) {
      errors.cheque_min = 'Minimum cheque must be numeric.';
    }
    if (formValues.cheque_max !== '' && Number.isNaN(chequeMaxValue)) {
      errors.cheque_max = 'Maximum cheque must be numeric.';
    }
    if (
      formValues.cheque_min !== '' &&
      formValues.cheque_max !== '' &&
      chequeMinValue > chequeMaxValue
    ) {
      errors.cheque_max =
        'Max cheque must be greater than or equal to min cheque.';
    }

    if (formValues.geography_focus.length === 0) {
      errors.geography_focus = 'Add at least one geography focus.';
    }

    if (formValues.stage_focus.length === 0) {
      errors.stage_focus = 'Add at least one stage focus.';
    }

    if (formValues.sector_focus.length === 0) {
      errors.sector_focus = 'Add at least one sector focus.';
    }

    const duplicateName = funders.find(
      (item) =>
        item.name?.trim().toLowerCase() ===
          formValues.name.trim().toLowerCase() &&
        item.id !== selectedFunder?.id,
    );
    if (duplicateName) {
      warnings.name = 'A funder with this name already exists.';
    }

    setFormErrors(errors);
    setFormWarnings(warnings);

    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const payload = {
      name: formValues.name,
      type: formValues.type,
      hq_country: formValues.hq_country,
      geography_focus: formValues.geography_focus,
      stage_focus: formValues.stage_focus,
      sector_focus: formValues.sector_focus,
      website_url: formValues.website_url || null,
      application_url: formValues.application_url || null,
      contact_name: formValues.contact_name || null,
      contact_email: formValues.contact_email || null,
      cheque_min:
        formValues.cheque_min === '' ? null : Number(formValues.cheque_min),
      cheque_max:
        formValues.cheque_max === '' ? null : Number(formValues.cheque_max),
      description: formValues.description || null,
      portfolio_examples: formValues.portfolio_examples?.join(',') || null,
      notes: formValues.notes || null,
      status: formValues.status,
    };

    const request =
      formMode === 'edit' || formMode === 'view'
        ? {
            key: `admin/funders/${selectedFunder?.id}`,
            method: 'put',
            data: payload,
          }
        : { key: 'admin/funders', method: 'post', data: payload };

    mutate(request, {
      onSuccess(res) {
        addToast(res);
        refetch();
        setShowForm(false);
        resetForm();
      },
      onError(err) {
        addToast(
          {
            message:
              err?.response?.data?.message ||
              'Unable to save funder. Please check the form and try again.',
          },
          'error',
        );
      },
    });
  };

  const handleToggleStatus = (funder) => {
    const nextStatus = funder.status === 'active' ? 'inactive' : 'active';
    mutate(
      {
        key: `admin/funders/${funder.id}`,
        method: 'patch',
        data: { status: nextStatus },
      },
      {
        onSuccess(res) {
          addToast(res);
          refetch();
        },
        onError(err) {
          addToast(
            {
              message:
                err?.response?.data?.message || 'Unable to change status.',
            },
            'error',
          );
        },
      },
    );
  };

  const handleCancel = () => {
    setShowForm(false);
    if (formMode === 'view') {
      setFormMode('create');
    }
    resetForm();
  };

  const handleSearch = () => {
    setPage(1);
    setSearchQuery(searchTerm.trim());
  };

  const allowAccess = accountType === 'admin' || accountType === 'sub_admin';

  if (!allowAccess) {
    return <PermissionBlinder />;
  }

  return (
    <>
      <Box>
        <Header title="Admin Funders" />
        <>
          <Flex w="100%">
            <Box className="flex w-full p-5 justify-end" mt="20px">
              {!showForm && (
                <BrandButton
                  onClick={openCreateForm}
                  fontSize="13px"
                  text="Add New Funder"
                  borderColor="#C7DFF0"
                  bg={'#134A70'}
                  colorScheme="blue"
                  w="full"
                  maxW="fit-content"
                />
              )}
            </Box>
          </Flex>

          <Box
            className={`hide-scroll ${
              IsDesktop ? 'max-h-[85vh] mt-5 px-5 pb-32 overflow-y-scroll' : ''
            }`}
          >
            {showForm ? (
              <Box className="flex p-5 flex-col" mt={8}>
                <FunderForm
                  values={formValues}
                  errors={formErrors}
                  warnings={formWarnings}
                  isLoading={isMutating}
                  surveydata={surveydata.data}
                  readOnly={formMode === 'view'}
                  onChange={handleFieldChange}
                  onMultiChange={handleMultiChange}
                  onSubmit={formMode === 'view' ? handleCancel : handleSave}
                  onCancel={handleCancel}
                  submitLabel={
                    formMode === 'view'
                      ? 'Close'
                      : formMode === 'edit'
                        ? 'Update Funder'
                        : 'Create Funder'
                  }
                />
              </Box>
            ) : (
              <Box>
                <Box className="flex flex-wrap flex-row gap-4 items-center mt-5">
                  <Input
                    placeholder="Search funders by name"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    width={{ base: '100%', md: '320px' }}
                  />

                  <BrandButton
                    onClick={handleSearch}
                    fontSize="13px"
                    text="Search"
                    borderColor="#C7DFF0"
                    bg={'#134A70'}
                    colorScheme="blue"
                    w="full"
                    maxW="fit-content"
                  />
                </Box>

                {isLoading ? (
                  <Flex justify="center" py={24}>
                    <Spinner />
                  </Flex>
                ) : (
                  <>
                    <Box className="flex flex-col w-full" overflowX="auto">
                      <TableContainer
                        mt="50px"
                        borderRadius="10px"
                        overflowX="auto"
                        className="w-full hide-scroll"
                      >
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>Name</Th>
                              <Th>Type</Th>
                              <Th>HQ Country</Th>
                              <Th>Status</Th>
                              <Th>Last Updated</Th>
                              <Th>Action</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {funders?.length > 0 ? (
                              funders.map((funder) => (
                                <Tr
                                  key={funder.id}
                                  _hover={{ bg: 'gray.50' }}
                                  cursor="pointer"
                                  onClick={() => openViewForm(funder)}
                                >
                                  <Td>{funder.name}</Td>
                                  <Td>{funder.type}</Td>
                                  <Td>{funder.hq_country || 'N/A'}</Td>
                                  <Td>
                                    <Text
                                      color={
                                        funder.status === 'active'
                                          ? 'green.600'
                                          : 'red.600'
                                      }
                                    >
                                      {funder.status === 'active'
                                        ? 'Active'
                                        : 'Inactive'}
                                    </Text>
                                  </Td>
                                  <Td>
                                    {funder.updatedAt
                                      ? moment(funder.updatedAt).format(
                                          'MMM D, YYYY',
                                        )
                                      : funder.createdAt
                                        ? moment(funder.createdAt).format(
                                            'MMM D, YYYY',
                                          )
                                        : '—'}
                                  </Td>
                                  <Td>
                                    <HStack spacing={2} flexWrap="wrap">
                                      <BrandButton
                                        size="sm"
                                        variant="outline"
                                        colorScheme="blue"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openViewForm(funder);
                                        }}
                                        fontSize="13px"
                                        text="View"
                                        borderColor="#C7DFF0"
                                        bg={'#134A70'}
                                        w="fit-content"
                                      />
                                      <BrandButton
                                        size="sm"
                                        colorScheme="brand.primary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openEditForm(funder);
                                        }}
                                        text="Edit"
                                        fontSize="13px"
                                        w="fit-content"
                                      />
                                      <BrandButton
                                        size="sm"
                                        fontSize="13px"
                                        w="fit-content"
                                        bg={
                                          funder.status === 'active'
                                            ? 'red'
                                            : 'green'
                                        }
                                        borderColor={
                                          funder.status === 'active'
                                            ? 'red'
                                            : 'green'
                                        }
                                        color={
                                          funder.status === 'active'
                                            ? 'white'
                                            : 'white'
                                        }
                                        variant="ghost"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleToggleStatus(funder);
                                        }}
                                        text={
                                          funder.status === 'active'
                                            ? 'Deactivate'
                                            : 'Activate'
                                        }
                                      />
                                    </HStack>
                                  </Td>
                                </Tr>
                              ))
                            ) : (
                              <Tr>
                                <Td colSpan={6} py={12}>
                                  <Text textAlign="center" color="gray.500">
                                    No funders found.
                                  </Text>
                                </Td>
                              </Tr>
                            )}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    </Box>

                    <Box mt={10}>
                      <PaginationControls
                        currentPage={page}
                        totalPages={Math.max(totalPages, 1)}
                        onPageChange={(nextPage) => {
                          if (nextPage >= 1 && nextPage <= totalPages) {
                            setPage(nextPage);
                          }
                        }}
                      />
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>
        </>
      </Box>
    </>
  );
};

export default Funders;
